import { useEffect, useRef, useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, ResponsiveContainer, Cell } from "recharts";

const ARCHETYPE_COLORS = {
  critico_interior: "#C62828",
  nino_herido: "#5C6BC0",
  protector: "#37474F",
  complacedor: "#F06292",
  guerrero: "#D32F2F",
  sabio: "#7E57C2",
  nino_libre: "#FFD54F",
  cuidador: "#81C784",
  perfeccionista: "#00897B",
  rebelde: "#F57C00",
  amante: "#E91E63",
  ermitano: "#78909C",
  estratega: "#5E35B1",
  sanador: "#26A69A",
  self: "#FFC107"
};

export default function ConstellationChart({ arquetipos, arquetiposData, conflictos }) {
  const [hoveredArchetype, setHoveredArchetype] = useState(null);
  const svgRef = useRef(null);

  // Position archetypes in a circular constellation pattern
  const positionedData = arquetipos.map((arq, index) => {
    const totalArqs = arquetipos.length;
    const angle = (index * 2 * Math.PI) / totalArqs - Math.PI / 2;
    const radius = 35 + (100 - arq.porcentaje) * 0.15; // More dominant = closer to center
    
    return {
      ...arq,
      x: 50 + radius * Math.cos(angle),
      y: 50 + radius * Math.sin(angle),
      z: arq.porcentaje * 3, // Size based on score
      name: arquetiposData[arq.arquetipo]?.nombre || arq.arquetipo,
      color: ARCHETYPE_COLORS[arq.arquetipo] || "#6A1B9A"
    };
  });

  // Self in center
  const selfData = {
    x: 50,
    y: 50,
    z: 150,
    name: "Self",
    arquetipo: "self",
    color: "#FFC107"
  };

  const allData = [...positionedData, selfData];

  // Get conflict lines
  const conflictLines = conflictos?.map(conflict => {
    const arq1 = positionedData.find(a => a.arquetipo === conflict.polo1);
    const arq2 = positionedData.find(a => a.arquetipo === conflict.polo2);
    if (arq1 && arq2) {
      return {
        x1: arq1.x,
        y1: arq1.y,
        x2: arq2.x,
        y2: arq2.y,
        intensity: conflict.intensidad
      };
    }
    return null;
  }).filter(Boolean) || [];

  // Custom dot component
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const isHovered = hoveredArchetype === payload.arquetipo;
    const size = (payload.z || 100) / 10;
    
    return (
      <g 
        onMouseEnter={() => setHoveredArchetype(payload.arquetipo)}
        onMouseLeave={() => setHoveredArchetype(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Glow effect */}
        <circle
          cx={cx}
          cy={cy}
          r={size + 8}
          fill={payload.color}
          opacity={isHovered ? 0.4 : 0.2}
          className="transition-all duration-300"
        />
        {/* Main dot */}
        <circle
          cx={cx}
          cy={cy}
          r={size}
          fill={payload.color}
          stroke={isHovered ? "#fff" : "rgba(255,255,255,0.3)"}
          strokeWidth={isHovered ? 2 : 1}
          className="transition-all duration-300"
          style={{
            filter: isHovered ? `drop-shadow(0 0 10px ${payload.color})` : 'none',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            transformOrigin: `${cx}px ${cy}px`
          }}
        />
        {/* Label */}
        {(isHovered || payload.arquetipo === 'self') && (
          <text
            x={cx}
            y={cy + size + 15}
            textAnchor="middle"
            fill="#F3E5F5"
            fontSize={11}
            fontFamily="Raleway, sans-serif"
            className="transition-opacity duration-300"
          >
            {payload.name}
          </text>
        )}
      </g>
    );
  };

  return (
    <div className="relative w-full h-full">
      {/* SVG for connection lines */}
      <svg 
        ref={svgRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Connection lines from Self to archetypes */}
        {positionedData.map((arq, index) => (
          <line
            key={`connection-${index}`}
            x1={50}
            y1={50}
            x2={arq.x}
            y2={arq.y}
            stroke={arq.color}
            strokeWidth={0.3}
            opacity={0.3}
            className="constellation-line"
          />
        ))}
        
        {/* Conflict lines */}
        {conflictLines.map((line, index) => (
          <line
            key={`conflict-${index}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="#D84315"
            strokeWidth={line.intensity === 'alta' ? 1 : 0.5}
            strokeDasharray={line.intensity === 'alta' ? "none" : "2,2"}
            opacity={0.6}
            className="constellation-line"
          />
        ))}
      </svg>

      {/* Recharts Scatter */}
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <XAxis 
            type="number" 
            dataKey="x" 
            domain={[0, 100]} 
            hide 
          />
          <YAxis 
            type="number" 
            dataKey="y" 
            domain={[0, 100]} 
            hide 
          />
          <ZAxis 
            type="number" 
            dataKey="z" 
            range={[100, 500]} 
          />
          <Scatter
            data={allData}
            shape={<CustomDot />}
          />
        </ScatterChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-wrap justify-center gap-2 p-2">
        {positionedData.slice(0, 5).map((arq) => (
          <div 
            key={arq.arquetipo}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-body"
            style={{ 
              backgroundColor: `${arq.color}20`,
              color: arq.color,
              border: `1px solid ${arq.color}40`
            }}
          >
            <span 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: arq.color }}
            />
            {arq.name}
          </div>
        ))}
      </div>

      {/* Conflict indicator */}
      {conflictLines.length > 0 && (
        <div className="absolute top-2 right-2 flex items-center gap-2 text-xs text-accent-glow">
          <span className="w-4 h-0.5 bg-accent" />
          <span className="font-body">Conflicto</span>
        </div>
      )}
    </div>
  );
}
